import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import { Alert, Card, Modal, OverlayTrigger, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { MdCheckCircle, MdInfoOutline, MdWarning } from 'react-icons/md';
import { ISCI, PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Button } from '@Components/Button';
import { Appearance, Design, Placement } from '@Components/designConstants';
import { PieSetter } from '@Pages/StreamingCreatives/PieSetter';
import { EditableCard } from '@Pages/StreamingCreatives/Cards';
import { CreativeMap } from '@src/redux/creative';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { getUncoveredRows } from '../blanket-traffic-utils';
import {
  CreativeOption,
  CreativePercent,
  CreativePercentMap,
  EditsMap,
  getCreativeOptions,
  getPlanRow,
  getRotationTotal,
  transformToHash,
  weekFormatter
} from '../linear-buying-utils';

import './CreativeAllocationModal.scss';

/** Whether one week's rotation is in a state that can be saved. */
interface WeekValidity {
  /** A week nobody has allocated against has nothing to save, so it can't be invalid. */
  hasAllocations: boolean;
  isValidTotal: boolean;
  isValidCount: boolean;
  isValid: boolean;
  /** How far off 100% the week's rotation is, for the error message. */
  offBy: number;
}

interface CreativeAllocationModalProps {
  /** One row per week. A single-week rotation is the one-element case. */
  planRows: PlanRow[];
  creativeMap: CreativeMap;
  colorMap: Record<string, string>;
  setEditsMap: Dispatch<SetStateAction<EditsMap>>;
  setNewRows: Dispatch<SetStateAction<Record<string, PlanRow>>>;
  /**
   * Whether a blanket traffic instruction already covers this row. Omitted when the feature is
   * off. A covered row draws its rotation from the instruction, so editing one here would write
   * a per-line rotation the network is never told about.
   */
  isBlanketCovered?: (row: PlanRow) => boolean;
  onClose: () => void;
}

export const CreativeAllocationModal = ({
  planRows,
  creativeMap,
  colorMap,
  setEditsMap,
  setNewRows,
  isBlanketCovered,
  onClose
}: CreativeAllocationModalProps) => {
  const isSingleWeek = planRows.length === 1;
  const editableRows = useMemo(
    () => getUncoveredRows(planRows, isBlanketCovered),
    [isBlanketCovered, planRows]
  );
  const editableWeeks = useMemo(
    () => new Set(editableRows.map(({ week: editableWeek }) => editableWeek)),
    [editableRows]
  );
  const coveredWeeks = planRows
    .map(({ week: planWeek }) => planWeek)
    .filter(planWeek => !editableWeeks.has(planWeek));
  const allWeeksCovered = editableRows.length === 0;

  const creativeOptions: Record<string, CreativeOption[]> = useMemo(
    () =>
      Object.fromEntries(
        planRows.map(planRow => [planRow.week, getCreativeOptions(planRow, creativeMap, colorMap)])
      ),
    [colorMap, creativeMap, planRows]
  );

  // Opening on a covered week would show a tab that isn't rendered.
  const [week, setWeek] = useState<string>(editableRows[0]?.week ?? planRows[0]?.week ?? '');

  const [iscisToPercentages, setIsciToPercentage] = useState<CreativePercentMap>({});

  const currentPercentageMap = useMemo(() => {
    let creativePercentMap: CreativePercentMap = {};
    for (const planRow of planRows) {
      // If there's an edited rotation, use that. Otherwise, use the original.
      const rotationToUse = planRow.edits?.creatives ?? planRow.creatives ?? [];
      for (const { isci, percent } of rotationToUse) {
        creativePercentMap = {
          ...creativePercentMap,
          ...transformToHash(planRow.week, isci, percent)
        };
      }
    }
    return creativePercentMap;
  }, [planRows]);

  // Combine existing rotation with changes and remove allocations that aren't valid.
  const sanitizedAllocations = useMemo(
    () => mergeAllocations(currentPercentageMap, iscisToPercentages),
    [iscisToPercentages, currentPercentageMap]
  );

  const allocationsByWeek = useMemo(() => {
    const byWeek: Record<string, CreativePercent[]> = {};
    for (const allocation of Object.values(sanitizedAllocations)) {
      byWeek[allocation.week] = [...(byWeek[allocation.week] || []), allocation];
    }
    return byWeek;
  }, [sanitizedAllocations]);

  const validityByWeek = useMemo(() => {
    const validity: Record<string, WeekValidity> = {};
    // Covered weeks are left out entirely: they have no editor, so they can neither be made valid
    // nor be allowed to block the save of the weeks that do.
    for (const planRow of editableRows) {
      const allocations = allocationsByWeek[planRow.week] || [];
      const { isValidTotal, offBy } = getRotationTotal(allocations);
      // Checks if there are more creatives than unit count. i.e. don't allow 50/50 rotation for 1
      // spot. Creatives zeroed out on their way off the rotation don't count against it.
      const isValidCount = allocations.filter(({ percent }) => percent > 0).length <= planRow.count;
      validity[planRow.week] = {
        hasAllocations: allocations.length > 0,
        isValidTotal,
        isValidCount,
        // An untouched week blocks nothing -- unlike a blanket rotation, it isn't being saved.
        isValid: allocations.length === 0 || (isValidTotal && isValidCount),
        offBy
      };
    }
    return validity;
  }, [allocationsByWeek, editableRows]);

  // Every week is saved together, so a broken rotation on a tab you aren't looking at has to block
  // the save too — otherwise it writes silently.
  const invalidWeeks = useMemo(
    () =>
      editableRows.map(({ week: planWeek }) => planWeek).filter(w => !validityByWeek[w]?.isValid),
    [editableRows, validityByWeek]
  );

  // A week with no allocations is skipped above so it can't block the weeks that do have them, but
  // if no week has any there's nothing to write at all — don't offer a save that does nothing.
  const nothingToSubmit = editableRows.every(({ week: w }) => !validityByWeek[w]?.hasAllocations);

  const setPercent = useCallback((allocationWeek: string, isci: string, percent: number) => {
    setIsciToPercentage(current => ({
      ...current,
      ...transformToHash(allocationWeek, isci, percent)
    }));
  }, []);

  const onSubmit = useCallback(() => {
    // If there are no changes, do nothing.
    if (R.isEmpty(iscisToPercentages)) {
      onClose();
      return;
    }

    // Construct metadata using the new rotation, per week. If an existing ISCI was edited, just
    // change the percent. If it's new, create new metadata for it.
    for (const weekKey of Object.keys(allocationsByWeek)) {
      const planRow = getPlanRow(planRows, weekKey);
      // allocationsByWeek is seeded from every week's existing rotation, so a covered week appears
      // here even though it was never editable. Writing it would hand the network a per-line
      // rotation for a week its blanket instruction already governs.
      if (!planRow || !editableWeeks.has(weekKey)) {
        continue;
      }
      const { key, isNewRow, creatives, plan_id } = planRow;

      const updatedRotation: ISCI[] = allocationsByWeek[weekKey].map(({ isci, percent }) => {
        const existingMetadata = (creatives || []).find(row => row.isci === isci);
        if (existingMetadata) {
          return { ...existingMetadata, percent };
        }
        return { plan_id: plan_id || null, isci, percent, isPending: true };
      });

      // If the updated rotation is the same as the original, skip this week.
      if (updatedRotation.length === 0 || R.equals(updatedRotation, creatives)) {
        continue;
      }

      const edit = { creatives: updatedRotation };
      if (isNewRow) {
        setNewRows(current => ({ ...current, [key]: { ...current[key], ...edit } }));
      } else {
        setEditsMap(current => ({
          ...current,
          [key]: { ...planRow, edits: { ...current[key]?.edits, ...edit } }
        }));
      }
    }
    onClose();
  }, [
    allocationsByWeek,
    editableWeeks,
    iscisToPercentages,
    planRows,
    setEditsMap,
    setNewRows,
    onClose
  ]);

  const weeklyCreativeOptions = (
    tabWeek: string
  ): (CreativeOption & { key: string; value: number })[] =>
    (creativeOptions[tabWeek] || []).map(creative => {
      const mapId = `${tabWeek}${creative.isci}`;
      return {
        ...creative,
        key: creative.isci,
        value: iscisToPercentages[mapId]?.percent ?? currentPercentageMap[mapId]?.percent ?? 0
      };
    });

  const { network, avail, rotation, length, count } = planRows[0];
  // With one week the spot count is fixed, so it belongs in the title. Across weeks it varies, so
  // each tab carries its own.
  const title = isSingleWeek
    ? `${network} - ${avail} - ${rotation} - ${length}s - ${count} spot(s)`
    : `${network} - ${avail} - ${rotation} - ${length}s`;

  const hasNonLiveCreative = (creativeOptions[week] || []).some(creative => creative.notLive);
  const selectedValidity = validityByWeek[week];
  const otherInvalidWeeks = invalidWeeks.filter(w => w !== week);

  return (
    <Modal show size='xl' onHide={onClose} className='creative-allocation-modal'>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className='title'>{title}</div>
          {hasNonLiveCreative && (
            <OverlayTrigger
              placement={Placement.RIGHT_START}
              overlay={
                <Tooltip id={`not-live-${network}`}>
                  There is at least one creative in this rotation that is not live on linear during
                  the currently selected week. You should probably remove it from the rotation.
                </Tooltip>
              }
            >
              <MdWarning color='orange' />
            </OverlayTrigger>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='modal-body'>
        {allWeeksCovered ? (
          <div className='blanket-covered-notice'>
            <MdInfoOutline />
            <div>
              <div className='blanket-covered-title'>Covered by a blanket traffic instruction</div>
              <div className='blanket-covered-detail'>
                {`${network} · ${avail} carries an approved pool of creative for these weeks, so this
                  row needs no rotation of its own. Change the pool from Blanket Traffic
                  Instructions in the page menu.`}
              </div>
            </div>
          </div>
        ) : (
          <div className='workspace'>
            {coveredWeeks.length > 0 && (
              <div className='blanket-covered-notice compact'>
                <MdInfoOutline />
                <div>
                  <div className='blanket-covered-title'>
                    {`${conditionallyPluralizeWithCount(
                      coveredWeeks.length,
                      'week'
                    )} covered by a blanket traffic instruction`}
                  </div>
                  <div className='blanket-covered-detail'>
                    {`${coveredWeeks
                      .map(weekFormatter)
                      .join(', ')} draw their creative from the approved pool for ${network} ·
                      ${avail}, so they have no tab here and are left untouched when you save.`}
                  </div>
                </div>
              </div>
            )}
            {editableRows.length === 1 ? (
              <WeekAllocation
                company={planRows[0].company}
                data={weeklyCreativeOptions(editableRows[0].week)}
                onChange={(isci, percent) => setPercent(editableRows[0].week, isci, percent)}
              />
            ) : (
              <Tabs defaultActiveKey={week} onSelect={selected => setWeek(selected ?? '')}>
                {editableRows.map(({ week: tabWeek, count: tabCount }) => (
                  <Tab
                    className='tab'
                    key={tabWeek}
                    eventKey={tabWeek}
                    title={
                      <div className='tab-title'>
                        {`${tabCount} ${weekFormatter(tabWeek)}`}
                        {validityByWeek[tabWeek]?.hasAllocations &&
                          validityByWeek[tabWeek]?.isValid && (
                            <div className='valid-indicator'>
                              <MdCheckCircle />
                            </div>
                          )}
                      </div>
                    }
                  >
                    <WeekAllocation
                      company={planRows[0].company}
                      data={weeklyCreativeOptions(tabWeek)}
                      onChange={(isci, percent) => setPercent(tabWeek, isci, percent)}
                    />
                  </Tab>
                ))}
              </Tabs>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!allWeeksCovered && selectedValidity && !selectedValidity.isValidTotal && (
          <Alert variant='danger'>{`Your rotation is off by ${selectedValidity.offBy}%`}</Alert>
        )}
        {!allWeeksCovered && selectedValidity && !selectedValidity.isValidCount && (
          <Alert variant='danger'>
            The rotation doesn&apos;t make sense with the current unit count
          </Alert>
        )}
        {!allWeeksCovered && otherInvalidWeeks.length > 0 && (
          <Alert variant='danger'>{`Fix the rotation for ${otherInvalidWeeks
            .map(w => Dfns.format('M/d', Dfns.parseISO(w)))
            .join(', ')} before saving.`}</Alert>
        )}
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
        {!allWeeksCovered && (
          <Button disabled={invalidWeeks.length > 0 || nothingToSubmit} onClick={onSubmit}>
            Okay
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

interface WeekAllocationProps {
  company: string;
  data: (CreativeOption & { key: string; value: number })[];
  onChange: (isci: string, percent: number) => void;
}

/** The pie and creative cards for a single week's rotation. */
const WeekAllocation = ({ company, data, onChange }: WeekAllocationProps) => (
  <Card className='workspace-card'>
    <div className='workspace-card-inner'>
      <PieSetter
        pieFlex={0.5}
        data={data}
        renderItem={elem => (
          <EditableCard
            {...elem}
            // Thumbnails are served per company, and a creative-map item doesn't carry one (the
            // map is keyed by company in redux). Without this the URL is built with an empty
            // company segment and every card reads "Thumbnail not found".
            company={company}
            onChange={(isci, value) => {
              // If you type 0, you get 0 as the value. But if you delete the value, it becomes
              // NaN. We want it to be 0 so that it doesn't get filtered out in the onSubmit.
              onChange(isci, Math.abs(value || 0));
            }}
          />
        )}
      />
    </div>
  </Card>
);

/** Layers the pending per-creative percent edits on top of the saved rotation. */
const mergeAllocations = (
  current: CreativePercentMap,
  edits: CreativePercentMap
): CreativePercentMap => {
  const merged: CreativePercentMap = R.clone(current);
  for (const key of Object.keys(edits)) {
    merged[key] = merged[key] ? { ...merged[key], percent: edits[key].percent } : edits[key];
  }
  return merged;
};
